import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function EventDebriefsModule() {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [aggregate, setAggregate] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ event_name: "", event_date: "", description: "" });

  const load = async () => {
    try {
      const r = await api.get("/debriefs");
      setList(r.data || []);
      if (!selectedId && r.data?.length > 0) setSelectedId(r.data[0].id);
    } catch { toast.error("Erreur chargement debriefs"); }
  };

  const loadAgg = async (did) => {
    if (!did) return;
    try {
      const r = await api.get(`/debriefs/${did}/aggregate`);
      setAggregate(r.data);
    } catch { toast.error("Erreur chargement résultats"); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadAgg(selectedId); }, [selectedId]);

  const createDebrief = async () => {
    if (!form.event_name || !form.event_date) {
      toast.error("Nom et date requis"); return;
    }
    try {
      const r = await api.post("/debriefs", form);
      toast.success(`✅ Debrief créé : ${r.data.debrief.event_name}`);
      setCreateOpen(false);
      setForm({ event_name: "", event_date: "", description: "" });
      await load();
      setSelectedId(r.data.debrief.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleStatus = async (status) => {
    try {
      await api.patch(`/debriefs/${selectedId}`, { status });
      toast.success(`Statut → ${status}`);
      await load();
      await loadAgg(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié 📋"); };

  const selected = list.find((d) => d.id === selectedId);
  const publicUrl = selected ? `${window.location.origin}/debrief/${selected.slug}` : "";

  const scoreColor = (s) => {
    if (s == null) return "text-zinc-600";
    if (s >= 8) return "text-emerald-300";
    if (s >= 5) return "text-amber-300";
    return "text-red-300";
  };

  return (
    <div className="space-y-6" data-testid="debriefs-module">
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">KAIZEN · DEBRIEF POST-EVENT ANONYME</div>
        <h1 className="text-3xl font-bold tracking-tight">Feedback équipe organisateurs</h1>
        <p className="text-zinc-500 text-sm mt-2">Recueille les vraies opinions de ton équipe AVANT la réunion. Anonyme. 5 min sur le téléphone. Les introvertis parlent enfin.</p>
        <Button onClick={() => setCreateOpen(true)} className="mt-4 bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="create-debrief-btn">
          + Nouveau debrief
        </Button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-2" data-testid="debriefs-list">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Debriefs ({list.length})</div>
          {list.length === 0 && <div className="text-xs text-zinc-600 italic">Aucun. Crée le premier (ex. "Gladiateurs du Nord V").</div>}
          {list.map((d) => (
            <button
              key={d.id}
              onClick={() => setSelectedId(d.id)}
              className={`w-full text-left border rounded-xl p-3 transition ${selectedId === d.id ? "border-amber-500/60 bg-amber-500/5" : "border-zinc-800 hover:border-zinc-700 bg-zinc-950/40"}`}
              data-testid={`debrief-card-${d.id}`}
            >
              <div className="text-sm font-medium truncate">{d.event_name}</div>
              <div className="text-xs text-zinc-500 mt-1">{d.event_date}</div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className={`text-[10px] ${d.status === "open" ? "border-emerald-500/40 text-emerald-300" : "border-zinc-600 text-zinc-400"}`}>{d.status}</Badge>
                <span className="text-[10px] text-amber-400">{d.responses_count || 0} réponses</span>
              </div>
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {!selected ? (
            <div className="border border-zinc-800 rounded-2xl p-10 text-center text-sm text-zinc-500">Sélectionne un debrief.</div>
          ) : (
            <>
              <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
                <h2 className="text-2xl font-bold">{selected.event_name}</h2>
                <div className="text-sm text-zinc-500 mt-1">{selected.event_date}</div>
                <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">URL PUBLIQUE ANONYME (envoyer à l'équipe)</div>
                    <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:underline break-all" data-testid="public-debrief-url">{publicUrl}</a>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copy(publicUrl)}>📋 Copier</Button>
                    {selected.status === "open" ? (
                      <Button size="sm" variant="outline" onClick={() => toggleStatus("closed")}>Fermer</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => toggleStatus("open")}>Rouvrir</Button>
                    )}
                  </div>
                </div>
              </div>

              {aggregate && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="agg-kpis">
                    <KPI label="Réponses" value={aggregate.responses_count} />
                    <KPI label="Score global" value={aggregate.overall_avg_score ? `${aggregate.overall_avg_score}/10` : "—"} accent />
                    <KPI label="Avec rôle" value={Object.keys(aggregate.roles_distribution || {}).length} />
                    <KPI label="Messages perso" value={aggregate.free_messages?.length || 0} />
                  </div>

                  <div className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40" data-testid="dept-summary">
                    <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">SCORES PAR DÉPARTEMENT</div>
                    <div className="space-y-2">
                      {aggregate.departments_summary.map((dept) => (
                        <div key={dept.id} className="flex items-center gap-3 border border-zinc-900 rounded-lg p-3">
                          <div className="flex-1">
                            <div className="text-sm text-zinc-200">{dept.label}</div>
                            <div className="text-[10px] text-zinc-500">{dept.count} réponse{dept.count > 1 ? "s" : ""}</div>
                          </div>
                          {dept.avg_score != null && (
                            <div className="text-right">
                              <div className={`text-2xl font-bold ${scoreColor(dept.avg_score)}`}>{dept.avg_score}<span className="text-xs text-zinc-500">/10</span></div>
                              <div className="text-[10px] text-zinc-500">min {dept.min_score} · max {dept.max_score}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-3" data-testid="open-answers">
                    <AnswerBlock title="✨ Ce qui a brillé" items={aggregate.open_answers.what_shined} tone="emerald" />
                    <AnswerBlock title="🔥 Ce qui a fait mal" items={aggregate.open_answers.what_hurt} tone="red" />
                    <AnswerBlock title="🎲 Ce qui a surpris" items={aggregate.open_answers.what_surprised} tone="amber" />
                  </div>

                  {aggregate.free_messages?.length > 0 && (
                    <div className="border border-purple-500/40 bg-purple-500/5 rounded-2xl p-5" data-testid="free-messages">
                      <div className="text-[10px] tracking-[0.3em] text-purple-300 mb-3">MESSAGES CONFIDENTIELS À KRU</div>
                      <div className="space-y-3">
                        {aggregate.free_messages.map((m, i) => (
                          <div key={i} className="border-l-2 border-purple-500/40 pl-3 text-sm text-zinc-300 italic">"{m}"</div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="create-debrief-dialog">
          <DialogHeader><DialogTitle>Nouveau debrief</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Nom de l'événement *</Label><Input value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} placeholder="Gladiateurs du Nord V" className="bg-black border-zinc-800" data-testid="form-event-name" /></div>
            <div><Label className="text-xs">Date de l'événement *</Label><Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="bg-black border-zinc-800" data-testid="form-event-date" /></div>
            <div><Label className="text-xs">Description / mot pour ton équipe</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Quelques mots qui rassurent ton équipe sur l'anonymat…" className="bg-black border-zinc-800 min-h-[80px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={createDebrief} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="form-submit-debrief">Créer + générer URL</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/50"}`}>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? "text-amber-300" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function AnswerBlock({ title, items, tone }) {
  const colorClass = tone === "emerald" ? "border-emerald-500/30 bg-emerald-500/5"
    : tone === "red" ? "border-red-500/30 bg-red-500/5"
    : "border-amber-500/30 bg-amber-500/5";
  return (
    <div className={`border rounded-2xl p-4 ${colorClass}`}>
      <div className="text-sm font-bold mb-3">{title}</div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-xs text-zinc-600 italic">Aucune réponse encore.</div>
        ) : (
          items.map((it, i) => (
            <div key={i} className="text-xs text-zinc-300 border-l border-white/10 pl-2 italic">"{it}"</div>
          ))
        )}
      </div>
    </div>
  );
}
